import { useEffect, useRef } from 'react';

export interface EditableMailPreviewProps {
  /** Fertiges Mail-HTML vom Server. Die editierbaren Stellen tragen data-edit="anrede|message|closing". */
  html: string;
  /** Ruft bei jeder Änderung den neuen Klartext des jeweiligen Feldes zurück. */
  onChange: (feld: 'anrede' | 'message' | 'closing' | 'kontoauszug', text: string) => void;
  /** Wird beim Verlassen eines Feldes gerufen — guter Moment, die Vorschau neu zu bauen. */
  onCommit?: () => void;
}

const FELDER = ['anrede', 'message', 'closing', 'kontoauszug'] as const;

/**
 * Zeigt die E-Mail so, wie der Kunde sie bekommt — und lässt Anrede, Nachricht und
 * Grußformel direkt darin schreiben.
 *
 * Warum Shadow DOM: Das Mail-HTML bringt eigene Inline-Styles mit und soll weder vom
 * App-CSS verändert werden noch selbst ins App-Layout ausstrahlen. Ein iframe wäre die
 * andere Möglichkeit, erlaubt aber kein direktes Bearbeiten von außen.
 */
export function EditableMailPreview({ html, onChange, onCommit }: EditableMailPreviewProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const shadowRef = useRef<ShadowRoot | null>(null);
  // Callbacks in Refs, damit ein neuer Elternrender nicht das ganze HTML neu setzt
  // (das würde beim Tippen den Cursor verlieren).
  const onChangeRef = useRef(onChange);
  const onCommitRef = useRef(onCommit);
  useEffect(() => { onChangeRef.current = onChange; onCommitRef.current = onCommit; });

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (!shadowRef.current) shadowRef.current = host.attachShadow({ mode: 'open' });
    const shadow = shadowRef.current;

    shadow.innerHTML = `
      <style>
        :host { display: block; }

        /* Bearbeitbare Stellen sind dauerhaft als solche erkennbar: feine gestrichelte
           Kontur, beim Überfahren ein Stift. Der feste Teil der Mail bleibt unberührt. */
        [data-edit] {
          position: relative;
          outline: none;
          border: 1px dashed #cbd5e1;
          border-radius: 6px;
          padding: 6px 30px 6px 8px;
          transition: border-color .12s, background-color .12s, box-shadow .12s;
        }
        [data-edit]::after {
          content: "✎";
          position: absolute;
          top: 4px;
          right: 7px;
          font-size: 12px;
          line-height: 1;
          color: #94a3b8;
          opacity: 0;
          transition: opacity .12s;
        }
        [data-edit]:hover { border-color: #60a5fa; background: rgba(37, 99, 235, .04); }
        [data-edit]:hover::after { opacity: 1; }
        [data-edit]:focus {
          border-style: solid;
          border-color: #2563eb;
          background: rgba(37, 99, 235, .06);
          box-shadow: 0 0 0 3px rgba(37, 99, 235, .12);
        }
        [data-edit]:focus::after { opacity: 0; }
        [data-edit]:empty::before { content: attr(data-platzhalter); color: #94a3b8; }
        /* Absätze am Rand der Felder nicht doppelt einrücken. */
        [data-edit] > p:first-child { margin-top: 0; }
        [data-edit] > p:last-child { margin-bottom: 0; }
      </style>
      ${html}
    `;
    // Links (z.B. der Unterschreiben-Button) in neuem Tab öffnen — sonst verlässt ein Klick
    // die App samt offenem Versand-Dialog.
    shadow.querySelectorAll('a[href]').forEach(a => { a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noopener noreferrer'); });

    const platzhalter: Record<string, string> = {
      anrede: 'Anrede…',
      message: 'Nachricht…',
      closing: 'Grußformel…',
      kontoauszug: 'Hinweis zum Kontoauszug…',
    };
    const aufraeumer: Array<() => void> = [];
    for (const feld of FELDER) {
      const el = shadow.querySelector<HTMLElement>(`[data-edit="${feld}"]`);
      if (!el) continue;
      el.setAttribute('contenteditable', 'true');
      el.spellcheck = true;
      el.setAttribute('role', 'textbox');
      el.setAttribute('aria-label', platzhalter[feld].replace('…', ''));
      el.setAttribute('data-platzhalter', platzhalter[feld]);
      el.title = 'Zum Bearbeiten hineinklicken';

      // Geschuetztes Leerzeichen zurueck in ein normales — sonst wandert es in den Mailtext.
      const beiEingabe = () => onChangeRef.current(feld, (el.innerText ?? '').replace(/\u00a0/g, ' ').trimEnd());
      const beiVerlassen = () => onCommitRef.current?.();
      // Einfuegen immer als Klartext — sonst landet fremdes Markup in der Mail.
      const beiEinfuegen = (e: ClipboardEvent) => {
        e.preventDefault();
        document.execCommand('insertText', false, e.clipboardData?.getData('text/plain') ?? '');
      };
      el.addEventListener('input', beiEingabe);
      el.addEventListener('blur', beiVerlassen);
      el.addEventListener('paste', beiEinfuegen as EventListener);
      aufraeumer.push(() => {
        el.removeEventListener('input', beiEingabe);
        el.removeEventListener('blur', beiVerlassen);
        el.removeEventListener('paste', beiEinfuegen as EventListener);
      });
    }
    return () => aufraeumer.forEach(f => f());
  }, [html]);

  return <div ref={hostRef} data-testid="mail-vorschau" />;
}
